import { useCallback, useMemo } from 'react'
import { ActiveFilter } from '@sequencehq/core-components'

/**
 * Custom hook to handle forced active filters combined with search filters
 *
 * @param forcedActiveFilters - Filters that are forced and cannot be changed by the user
 * @param searchActiveFilters - Filters from the search params that the user can modify
 * @param onChangeSearchActiveFilters - Handler to update search filters
 * @returns Combined active filters and a handler that respects forced filters
 */
export function useForcedActiveFilters({
  forcedActiveFilters,
  searchActiveFilters,
  onChangeSearchActiveFilters
}: {
  forcedActiveFilters: ActiveFilter[]
  searchActiveFilters: ActiveFilter[]
  onChangeSearchActiveFilters: (filters: ActiveFilter[]) => void
}) {
  // Combine forced filters with search filters
  const activeFilters = useMemo(
    () => [...forcedActiveFilters, ...searchActiveFilters],
    [forcedActiveFilters, searchActiveFilters]
  )

  // Custom handler for filter changes that respects forced filters
  const onChangeActiveFilters = useCallback(
    (newActiveFilters: ActiveFilter[]) => {
      // Only update filters that aren't in forcedActiveFilters
      onChangeSearchActiveFilters(
        newActiveFilters.filter(
          filter =>
            !forcedActiveFilters.find(
              forcedFilter => forcedFilter.paramName === filter.paramName
            )
        )
      )
    },
    [onChangeSearchActiveFilters, forcedActiveFilters]
  )

  return {
    activeFilters,
    onChangeActiveFilters
  }
}
